import { useEffect, useMemo, useState } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import { useAuth } from "../../context/AuthContext";
import { profileService } from "../../services/profileService";
import { toast } from "react-hot-toast";
import { LedgerField, LedgerSection } from "./LedgerSection";

const PINCODE_RE = /^\d{6}$/;
const PHONE_RE   = /^[+]?[\d\s-]{7,15}$/;

function readEmergency(addr: unknown): string {
  if (addr && typeof addr === "object" && "emergencyNumber" in addr) {
    return String((addr as { emergencyNumber?: string }).emergencyNumber ?? "");
  }
  return "";
}

export default function UserAddressCard() {
  const { isOpen, openModal, closeModal } = useModal();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);

  const buildFormData = () => ({
    country: user?.address?.country || user?.country || "",
    city:    user?.address?.city    || user?.city    || "",
    state:   user?.address?.state   || user?.state   || "",
    taxId:   user?.taxId || user?.taxNumber || "",
    street:      user?.address?.street      || user?.street      || "",
    apartment:   user?.address?.apartment   || user?.apartment   || "",
    postalCode:  user?.address?.postalCode  || user?.postalCode  || user?.zipCode || "",
    emergencyNumber: readEmergency(user?.address),
  });

  const [formData, setFormData] = useState(buildFormData());

  useEffect(() => {
    if (isOpen) setFormData(buildFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const pincodeError  = useMemo(() => { const v = formData.postalCode.trim(); if (!v) return null; return PINCODE_RE.test(v) ? null : "Enter a valid 6-digit pincode."; }, [formData.postalCode]);
  const emergencyError = useMemo(() => { const v = formData.emergencyNumber.trim(); if (!v) return null; return PHONE_RE.test(v) ? null : "Enter a valid phone number (7–15 digits)."; }, [formData.emergencyNumber]);
  const canSave = !saving && !pincodeError && !emergencyError;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const result = await profileService.updateProfile({
        address: {
          country: formData.country,
          city:    formData.city,
          state:   formData.state,
          taxId:   formData.taxId,
          street:         formData.street.trim(),
          apartment:      formData.apartment.trim(),
          postalCode:     formData.postalCode.trim(),
          emergencyNumber: formData.emergencyNumber.trim(),
        },
      });
      if (result.success) {
        toast.success("Address updated");
        if (result.data) {
          updateUser(result.data);
        } else {
          updateUser({
            address: {
              country: formData.country, city: formData.city, state: formData.state,
              street: formData.street.trim(), apartment: formData.apartment.trim(),
              postalCode: formData.postalCode.trim(), emergencyNumber: formData.emergencyNumber.trim(),
            },
            taxId: formData.taxId,
          });
        }
        closeModal();
      } else { toast.error(result.message); }
    } catch { toast.error("Failed to update address"); }
    finally { setSaving(false); }
  };

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const userStreet     = user?.address?.street    || user?.street    || "";
  const userApartment  = user?.address?.apartment || user?.apartment || "";
  const userPostalCode = user?.address?.postalCode || user?.postalCode || user?.zipCode || "";
  const userEmergency  = readEmergency(user?.address);

  return (
    <>
      <LedgerSection
        title="Address & Emergency Contact"
        subtitle="Where you're based and who to reach in an emergency"
        onEdit={openModal}
      >
        <LedgerField fullWidth label="Full Address" value={userStreet} />
        <LedgerField label="Apartment / Suite" value={userApartment} />
        <LedgerField label="Pincode" value={userPostalCode} />
        <LedgerField fullWidth label="Emergency Number" value={userEmergency} />
      </LedgerSection>

      {/* ── EDIT MODAL ───────────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[640px] m-4">
        <div className="relative w-full p-4 overflow-y-auto bg-white no-scrollbar rounded-3xl dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              Edit Address & Emergency Contact
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 lg:mb-7">
              Update your address, pincode, apartment, and emergency contact number.
            </p>
          </div>
          <form className="flex flex-col" onSubmit={(e) => { e.preventDefault(); void handleSave(); }}>
            <div className="px-2 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                <div className="lg:col-span-2">
                  <Label>Full Address</Label>
                  <textarea
                    rows={2}
                    value={formData.street}
                    onChange={(e) => handleInputChange("street", e.target.value)}
                    placeholder="House / building, street, area, city, state, country"
                    className="dark:bg-dark-900 h-auto w-full resize-y rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#a3cd39] focus:outline-hidden focus:ring-3 focus:ring-[#a3cd39]/20 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-white/30"
                  />
                </div>
                <div>
                  <Label>Apartment / Suite</Label>
                  <Input type="text" value={formData.apartment} onChange={(e) => handleInputChange("apartment", e.target.value)} placeholder="Apartment, suite, unit (optional)" />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input type="text" value={formData.postalCode} onChange={(e) => handleInputChange("postalCode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))} placeholder="6-digit pincode" />
                  {pincodeError && <p className="mt-1 text-xs font-medium text-rose-600">{pincodeError}</p>}
                </div>
                <div className="lg:col-span-2">
                  <Label>Emergency Number</Label>
                  <Input type="tel" value={formData.emergencyNumber} onChange={(e) => handleInputChange("emergencyNumber", e.target.value)} placeholder="e.g. +91 98765 43210" />
                  {emergencyError
                    ? <p className="mt-1 text-xs font-medium text-rose-600">{emergencyError}</p>
                    : <p className="mt-1 text-xs text-gray-400">Whom should we contact in case of an emergency.</p>}
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3 px-2 lg:justify-end">
              <Button size="sm" variant="outline" onClick={closeModal} disabled={saving}>Close</Button>
              <Button size="sm" onClick={handleSave} disabled={!canSave} type="submit">{saving ? "Saving…" : "Save Changes"}</Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
